import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HiBell } from 'react-icons/hi';
import { getApiUrl } from '../../config/env';
import { useLanguage } from '../../contexts/LanguageContext';
import './notificationDropdown.scss';

interface Notification {
  id: number;
  type: string;
  subject: string;
  body: string;
  isRead: boolean;
  createdAt: string;
  recipient?: string;
}

interface PaginatedResult {
  items: Notification[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
}

const NotificationDropdown: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasError, setHasError] = useState(false); // Track if there was an error to prevent infinite retries
  const [hasFetched, setHasFetched] = useState(false); // Track if initial fetch was attempted
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsListRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 20;

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || '';
  };

  // Fetch notifications - removed isLoading and hasMore from dependencies to prevent infinite loops
  const fetchNotifications = useCallback(async (page: number, forceRefresh = false) => {
    // Get current state values to check conditions
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token available for notifications');
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetch(
        getApiUrl(`/Notification?pageNumber=${page}&pageSize=${PAGE_SIZE}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept-Language': language
          }
        }
      );

      // Handle 401 specifically - don't retry
      if (response.status === 401) {
        console.error('Notifications: Unauthorized (401) - stopping requests');
        setHasError(true);
        setHasMore(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }

      const data = await response.json();
      const result = data.data as PaginatedResult;

      if (page === 1 || forceRefresh) {
        setNotifications(result.items);
      } else {
        setNotifications(prev => [...prev, ...result.items]);
      }

      setTotalItems(result.totalItems);
      setHasMore(result.items.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setHasError(true);
      setHasMore(false); // Stop trying to load more on error
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [language]); // Added language dependency

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        getApiUrl('/Notification/unread-count'),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept-Language': language
          }
        }
      );

      // Silently handle 401 - don't retry
      if (response.status === 401) {
        console.error('Unread count: Unauthorized (401)');
        return;
      }

      if (!response.ok) return;

      const data = await response.json();
      setUnreadCount(data.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [language]); // Added language dependency

  // Load initial notifications and unread count on mount
  useEffect(() => {
    if (!hasFetched && !hasError && !isLoading) {
      fetchNotifications(1);
      fetchUnreadCount();
    }
  }, [hasFetched, hasError, isLoading, fetchNotifications, fetchUnreadCount]);

  // Keep unread count updated - removed reset logic as we want it on load
  useEffect(() => {
    fetchUnreadCount();
  }, [language, fetchUnreadCount]);

  // Handle infinite scroll - use refs to avoid dependency issues
  const loadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const hasErrorRef = useRef(hasError);
  const currentPageRef = useRef(currentPage);

  // Keep refs in sync
  useEffect(() => {
    loadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
    hasErrorRef.current = hasError;
    currentPageRef.current = currentPage;
  }, [isLoading, hasMore, hasError, currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (!notificationsListRef.current) return;
      // Don't load more if there's an error, already loading, or no more items
      if (hasErrorRef.current || loadingRef.current || !hasMoreRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = notificationsListRef.current;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage > 0.8) { // Changed to 0.8 for better UX
        const nextPage = currentPageRef.current + 1;
        setCurrentPage(nextPage);
        fetchNotifications(nextPage);
      }
    };

    const listElement = notificationsListRef.current;
    if (listElement && isOpen) {
      listElement.addEventListener('scroll', handleScroll);
      return () => listElement.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      const response = await fetch(
        getApiUrl(`/Notification/${notificationId}/mark-as-read`),
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
            'Accept-Language': language
          }
        }
      );

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
        );
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <button
        className="notification-btn"
        onClick={() => setIsOpen(!isOpen)}
        title={t('navbar.notifications')}
      >
        <HiBell className="notification-icon" />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header">
            <h3>{t('common:navbar.notifications')}</h3>
            <span className="notification-count">({unreadCount})</span>
          </div>

          <div className="notification-list" ref={notificationsListRef}>
            {notifications.length === 0 && !isLoading ? (
              <div className="no-notifications">
                <p>{t('notifications:notifications.noNotifications')}</p>
              </div>
            ) : notifications.length === 0 && isLoading ? (
              <div className="loading-indicator">{t('common:common.loading')}</div>
            ) : (
              <>
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                    onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}
                  >
                    <div className="notification-content">
                      <div className="notification-type">{notification.type}</div>
                      <h4 className="notification-subject">{notification.subject}</h4>
                      {/* TEMP: body hidden in the list for now - restore this line to bring it back */}
                      {/* <p className="notification-body">{notification.body}</p> */}
                      <span className="notification-time">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {!notification.isRead && <div className="unread-indicator"></div>}
                  </div>
                ))}
                {isLoading && hasMore && <div className="loading-indicator">{t('common:common.loading')}</div>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
